import { Card, Table, Space, Typography, Tag, Button, Tooltip, Input, Select, InputNumber } from "antd";
import { DownloadOutlined, EyeOutlined, MailOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useCpdUserProgress } from "../../../hooks/cpd";
import { fetchCpdUserProgress } from "../../../services/cpd";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { useAtom } from "jotai";
import { userDesignationsQueryAtom, districtsQueryAtom, hospitalsQueryAtom } from "../../../services/queries";

const { Text, Title } = Typography;
const { Option } = Select;

const formatLastActivity = (date) => {
  if (!date) return "N/A";
  
  // Check if date is 1970-01-01 (Unix epoch), treat as never active
  if (dayjs(date).isSame(dayjs('1970-01-01'), 'day')) {
    return "Never";
  }
  
  return dayjs(date).fromNow();
};

const formatLastActivityForExport = (date) => {
  if (!date) return "";
  
  // Check if date is 1970-01-01 (Unix epoch), treat as never active
  if (dayjs(date).isSame(dayjs('1970-01-01'), 'day')) {
    return "Never";
  }
  
  return dayjs(date).format("DD MMM YYYY HH:mm");
};

const CpdUserProgressPanel = ({ filters }) => {
  const [pagination, setPagination] = useState({ page: 0, size: 20 });
  const [searchKeyword, setSearchKeyword] = useState("");
  const [sortField, setSortField] = useState("totalCpdPoints");
  const [sortOrder, setSortOrder] = useState("desc");
  const [exportLoading, setExportLoading] = useState(false);

  // Dedicated filters for user progress
  const [gender, setGender] = useState(null);
  const [designationUid, setDesignationUid] = useState(null);
  const [hospitalUid, setHospitalUid] = useState(null);
  const [districtUid, setDistrictUid] = useState(null);
  const [minCpdPoints, setMinCpdPoints] = useState(null);
  const [maxCpdPoints, setMaxCpdPoints] = useState(null);
  const [isEmailVerified, setIsEmailVerified] = useState(null);

  // Fetch filter options
  const [{ data: designations }] = useAtom(userDesignationsQueryAtom, { delay: 0 });
  const [{ data: districts }] = useAtom(districtsQueryAtom, { delay: 0 });
  const [{ data: hospitals }] = useAtom(hospitalsQueryAtom, { delay: 0 });

  const { data, isLoading, isFetching } = useCpdUserProgress({
    ...filters,
    ...pagination,
    searchKeyword,
    sortField,
    sortOrder,
    gender,
    designationUid,
    hospitalUid,
    districtUid,
    minCpdPoints,
    maxCpdPoints,
    isEmailVerified,
  });

  const columns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      fixed: "left",
      width: 200,
      sorter: true,
      sortOrder: sortField === "fullName" ? (sortOrder === "asc" ? "ascend" : "descend") : null,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.email}
            {record.isEmailVerified && (
              <MailOutlined style={{ color: "#52c41a", marginLeft: 4 }} />
            )}
          </Text>
        </Space>
      ),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      width: 100,
      filters: [
        { text: "Male", value: "MALE" },
        { text: "Female", value: "FEMALE" },
        { text: "Not Specified", value: "NOT_SPECIFIED" },
      ],
      render: (gender) => {
        const colorMap = { MALE: "blue", FEMALE: "pink", NOT_SPECIFIED: "default" };
        return <Tag color={colorMap[gender] || "default"}>{gender || "N/A"}</Tag>;
      },
    },
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      width: 150,
      ellipsis: true,
      render: (text, record) => (
        <Tooltip title={record.customDesignation}>
          <Text>{text || "N/A"}</Text>
          {record.customDesignation && (
            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
              ({record.customDesignation})
            </Text>
          )}
        </Tooltip>
      ),
    },
    {
      title: "Hospital",
      dataIndex: "hospital",
      key: "hospital",
      width: 150,
      ellipsis: true,
      render: (text) => text || "N/A",
    },
    {
      title: "District",
      dataIndex: "district",
      key: "district",
      width: 120,
      ellipsis: true,
      render: (text) => text || "N/A",
    },
    {
      title: "In Depth Learning",
      dataIndex: "completedQuizzes",
      key: "completedQuizzes",
      width: 100,
      align: "right",
      sorter: true,
      sortOrder: sortField === "completedQuizzes" ? (sortOrder === "asc" ? "ascend" : "descend") : null,
      render: (value) => <Tag color="blue">{value || 0}</Tag>,
    },
    {
      title: "Cases",
      dataIndex: "completedCases",
      key: "completedCases",
      width: 100,
      align: "right",
      sorter: true,
      sortOrder: sortField === "completedCases" ? (sortOrder === "asc" ? "ascend" : "descend") : null,
      render: (value) => <Tag color="green">{value || 0}</Tag>,
    },
    {
      title: "Guidelines",
      dataIndex: "completedGuidelines",
      key: "completedGuidelines",
      width: 110,
      align: "right",
      sorter: true,
      sortOrder: sortField === "completedGuidelines" ? (sortOrder === "asc" ? "ascend" : "descend") : null,
      render: (value) => <Tag color="orange">{value || 0}</Tag>,
    },
    {
      title: "CPD Points",
      dataIndex: "totalCpdPoints",
      key: "totalCpdPoints",
      width: 120,
      align: "right",
      sorter: true,
      sortOrder: sortField === "totalCpdPoints" ? (sortOrder === "asc" ? "ascend" : "descend") : null,
      render: (value) => (
        <Text strong style={{ color: "#1890ff", fontSize: 16 }}>
          {value || 0}
        </Text>
      ),
    },
    {
      title: "Last Activity",
      dataIndex: "lastActivity",
      key: "lastActivity",
      width: 150,
      sorter: true,
      sortOrder: sortField === "lastActivity" ? (sortOrder === "asc" ? "ascend" : "descend") : null,
      render: (date) =>
        date ? (
          <Tooltip title={dayjs(date).format("DD MMM YYYY HH:mm:ss")}>
            <Text type="secondary">{formatLastActivity(date)}</Text>
          </Tooltip>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Joined",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 120,
      sorter: true,
      sortOrder: sortField === "createdDate" ? (sortOrder === "asc" ? "ascend" : "descend") : null,
      render: (date) =>
        date ? (
          <Tooltip title={dayjs(date).format("DD MMM YYYY HH:mm:ss")}>
            <Text type="secondary">{dayjs(date).format("DD MMM YYYY")}</Text>
          </Tooltip>
        ) : (
          "N/A"
        ),
    },
  ];

  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination({
      page: newPagination.current - 1,
      size: newPagination.pageSize,
    });

    // Handle sorting
    if (sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
    } else {
      // Reset to default sorting
      setSortField("totalCpdPoints");
      setSortOrder("desc");
    }
  };

  const handleSearch = (value) => {
    setSearchKeyword(value);
    // Reset to first page when searching
    setPagination({ page: 0, size: pagination.size });
  };

  const handleFilterChange = (filterType, value) => {
    // Reset to first page when any filter changes
    setPagination({ page: 0, size: pagination.size });

    switch (filterType) {
      case 'gender':
        setGender(value);
        break;
      case 'designationUid':
        setDesignationUid(value);
        break;
      case 'hospitalUid':
        setHospitalUid(value);
        break;
      case 'districtUid':
        setDistrictUid(value);
        break;
      case 'minCpdPoints':
        setMinCpdPoints(value);
        break;
      case 'maxCpdPoints':
        setMaxCpdPoints(value);
        break;
      case 'isEmailVerified':
        setIsEmailVerified(value);
        break;
      default:
        break;
    }
  };

  const clearFilters = () => {
    setSearchKeyword("");
    setGender(null);
    setDesignationUid(null);
    setHospitalUid(null);
    setDistrictUid(null);
    setMinCpdPoints(null);
    setMaxCpdPoints(null);
    setIsEmailVerified(null);
    setPagination({ page: 0, size: pagination.size });
  };

  const handleExportToExcel = async () => {
    if (exportLoading) return;

    setExportLoading(true);
    try {
      // Build query params the same way as the hook
      const baseParams = {
        ...filters,
        searchKeyword,
        sortField,
        sortOrder,
      };

      // Transform params to API format (same as hook)
      const transformParams = (params) => {
        const {
          page = 0,
          size = 20,
          dateRange,
          gender,
          designationUid,
          hospitalUid,
          districtUid,
          minCpdPoints,
          maxCpdPoints,
          isEmailVerified,
          searchKeyword,
          sortField,
          sortOrder,
        } = params;

        const qp = { page, size };

        if (dateRange?.length === 2) {
          qp.startDate = dateRange[0].toISOString();
          qp.endDate = dateRange[1].toISOString();
        }

        if (gender) qp.gender = gender;
        if (designationUid) qp.designationUid = designationUid;
        if (hospitalUid) qp.hospitalUid = hospitalUid;
        if (districtUid) qp.districtUid = districtUid;
        if (minCpdPoints !== undefined) qp.minCpdPoints = minCpdPoints;
        if (maxCpdPoints !== undefined) qp.maxCpdPoints = maxCpdPoints;
        if (isEmailVerified !== undefined) qp.isEmailVerified = isEmailVerified;
        if (searchKeyword) qp.searchKeyword = searchKeyword;
        if (sortField) qp.sortField = sortField;
        if (sortOrder) qp.sortOrder = sortOrder;

        return qp;
      };

      // Fetch all filtered data by looping through pages
      const allUsers = [];
      let currentPage = 0;
      let hasMorePages = true;

      while (hasMorePages) {
        const pageParams = transformParams({ ...baseParams, page: currentPage, size: 100 });
        const pageResponse = await fetchCpdUserProgress(pageParams);
        const pageData = pageResponse.content || [];

        allUsers.push(...pageData);

        // Check if there are more pages
        const totalPages = Math.ceil(pageResponse.totalElements / 100);
        hasMorePages = currentPage < totalPages - 1 && pageData.length === 100;
        currentPage++;
      }

      if (allUsers.length === 0) {
        return;
      }

      const exportData = allUsers.map((user) => ({
        "Full Name": user.fullName,
        Email: user.email,
        "Email Verified": user.isEmailVerified ? "Yes" : "No",
        Gender: user.gender,
        Designation: user.designation,
        "Custom Designation": user.customDesignation || "",
        Hospital: user.hospital || "",
        District: user.district || "",
        "Completed In Depth Learning": user.completedQuizzes,
        "Completed Cases": user.completedCases,
        "Completed Guidelines": user.completedGuidelines,
        "Total CPD Points": user.totalCpdPoints,
        "Last Activity": formatLastActivityForExport(user.lastActivity),
        "Joined Date": user.createdDate
          ? dayjs(user.createdDate).format("DD MMM YYYY")
          : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "CPD User Progress");

      // Auto-size columns
      const maxWidth = 50;
      const colWidths = Object.keys(exportData[0] || {}).map((key) => {
        const maxLen = Math.max(
          key.length,
          ...exportData.map((row) => String(row[key] || "").length)
        );
        return { wch: Math.min(maxLen + 2, maxWidth) };
      });
      worksheet["!cols"] = colWidths;

      const filename = `cpd-user-progress-${dayjs().format("YYYY-MM-DD-HHmmss")}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Card
      title={
        <Space>
          <EyeOutlined />
          <Title level={4} style={{ margin: 0 }}>
            User Progress Details
          </Title>
          <Text type="secondary">
            ({data?.totalElements?.toLocaleString() || 0} users)
          </Text>
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportToExcel}
            disabled={!data?.content || data.content.length === 0}
            loading={exportLoading}
          >
            Export to Excel
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%", marginBottom: 16 }}>
        <Card size="small" style={{ backgroundColor: "#fafafa" }}>
          <Space wrap>
            <Input.Search
              placeholder="Search by name or email..."
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: 250 }}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onSearch={handleSearch}
              loading={isFetching}
            />

            <Select
              placeholder="Gender"
              allowClear
              style={{ width: 120 }}
              value={gender}
              onChange={(value) => handleFilterChange('gender', value)}
            >
              <Option value="MALE">Male</Option>
              <Option value="FEMALE">Female</Option>
              <Option value="NOT_SPECIFIED">Not Specified</Option>
            </Select>

            <Select
              placeholder="Designation"
              allowClear
              style={{ width: 140 }}
              value={designationUid}
              onChange={(value) => handleFilterChange('designationUid', value)}
              loading={!designations}
            >
              {designations?.map((designation) => (
                <Option key={designation.uid} value={designation.uid}>
                  {designation.name}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="District"
              allowClear
              style={{ width: 140 }}
              value={districtUid}
              onChange={(value) => handleFilterChange('districtUid', value)}
              loading={!districts}
            >
              {districts?.map((district) => (
                <Option key={district.uid} value={district.uid}>
                  {district.name}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="Hospital"
              allowClear
              style={{ width: 140 }}
              value={hospitalUid}
              onChange={(value) => handleFilterChange('hospitalUid', value)}
              loading={!hospitals}
            >
              {hospitals?.map((hospital) => (
                <Option key={hospital.uid} value={hospital.uid}>
                  {hospital.name}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="Email Verified"
              allowClear
              style={{ width: 140 }}
              value={isEmailVerified}
              onChange={(value) => handleFilterChange('isEmailVerified', value)}
            >
              <Option value={true}>Verified</Option>
              <Option value={false}>Not Verified</Option>
            </Select>

            <InputNumber
              placeholder="Min CPD Points"
              min={0}
              style={{ width: 140 }}
              value={minCpdPoints}
              onChange={(value) => handleFilterChange('minCpdPoints', value)}
            />

            <InputNumber
              placeholder="Max CPD Points"
              min={0}
              style={{ width: 140 }}
              value={maxCpdPoints}
              onChange={(value) => handleFilterChange('maxCpdPoints', value)}
            />

            <Button onClick={clearFilters} icon={<FilterOutlined />}>
              Clear Filters
            </Button>
          </Space>
        </Card>
      </Space>

      <Table
        columns={columns}
        dataSource={data?.content || []}
        loading={isLoading || isFetching}
        rowKey="uid"
        pagination={{
          current: pagination.page + 1,
          pageSize: pagination.size,
          total: data?.totalElements || 0,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} users`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1500, y: 600 }}
        size="small"
      />
    </Card>
  );
};

export default CpdUserProgressPanel;
